export type ReviewSource = 'Google' | 'Yelp' | 'Trustpilot' | 'Facebook' | 'Manual';
export type Sentiment = 'positive' | 'neutral' | 'negative';
export type RiskLevel = 'low' | 'medium' | 'high';
export type Urgency = 'low' | 'medium' | 'high';
export type ReviewStatus = 'new' | 'drafted' | 'needs_review' | 'approved' | 'published' | 'escalated';
export type BrandVoice = 'warm' | 'professional' | 'concise';

export interface BusinessProfile {
  businessName: string;
  industry: string;
  location: string;
  brandVoice: BrandVoice;
  monthlyReviews: number;
  averageTicket: number;
  laborCostPerHour: number;
  plan: 'starter' | 'growth' | 'agency';
}

export interface Review {
  id: string;
  source: ReviewSource;
  location: string;
  reviewerName: string;
  rating: number;
  comment: string;
  createdAt: string;
  status: ReviewStatus;
  responseDraft?: string;
  approvedAt?: string;
  publishedAt?: string;
}

export interface ReviewAnalysis {
  sentiment: Sentiment;
  risk: RiskLevel;
  urgency: Urgency;
  score: number;
  tags: string[];
  reasons: string[];
  redactedComment: string;
  recommendedTone: BrandVoice;
  timeToRespondHours: number;
}

export interface Metrics {
  totalReviews: number;
  unhandledReviews: number;
  highRiskReviews: number;
  responseCoverage: number;
  approvalRate: number;
  averageRating: number;
  estimatedMonthlyHoursSaved: number;
  estimatedMonthlyLaborValue: number;
  estimatedRevenueAtRisk: number;
  roiNarrative: string;
}

const CATEGORY_RULES: Array<{ tag: string; needles: string[] }> = [
  { tag: 'service', needles: ['rude', 'attitude', 'ignored', 'staff', 'manager', 'service', 'trato', 'mal atendido'] },
  { tag: 'speed', needles: ['wait', 'late', 'slow', 'delay', 'espera', 'tarde', 'retraso'] },
  { tag: 'quality', needles: ['broken', 'cold', 'dirty', 'quality', 'poor', 'malo', 'sucio', 'frio'] },
  { tag: 'billing', needles: ['charged', 'refund', 'invoice', 'price', 'expensive', 'cobro', 'factura', 'reembolso'] },
  { tag: 'safety', needles: ['unsafe', 'injury', 'danger', 'allergy', 'food poisoning', 'privacy', 'peligro', 'lesion'] },
  { tag: 'loyalty', needles: ['never again', 'lost customer', 'cancel', 'no volver', 'cliente perdido'] }
];

const HIGH_RISK_TERMS = [
  'legal',
  'lawsuit',
  'attorney',
  'discrimination',
  'racist',
  'privacy',
  'gdpr',
  'unsafe',
  'injury',
  'food poisoning',
  'fraud',
  'scam',
  'chargeback',
  'denuncia',
  'abogado',
  'discriminacion',
  'intoxicacion',
  'estafa'
];

const MEDIUM_RISK_TERMS = [
  'refund',
  'manager',
  'complaint',
  'angry',
  'terrible',
  'awful',
  'unacceptable',
  'reembolso',
  'queja',
  'fatal',
  'horrible'
];

export const DEFAULT_PROFILE: BusinessProfile = {
  businessName: 'Northstar Dental Studio',
  industry: 'Local healthcare clinic',
  location: 'Madrid, Spain',
  brandVoice: 'professional',
  monthlyReviews: 42,
  averageTicket: 180,
  laborCostPerHour: 28,
  plan: 'starter'
};

export const PLAN_LIMITS = {
  starter: { label: 'Starter', price: 29, locations: 1, reviews: 75, teamSeats: 2 },
  growth: { label: 'Growth', price: 79, locations: 3, reviews: 300, teamSeats: 8 },
  agency: { label: 'Agency', price: 199, locations: 15, reviews: 2_000, teamSeats: 25 }
} as const;

export function clampRating(rating: number): number {
  if (!Number.isFinite(rating)) return 3;
  return Math.min(5, Math.max(1, Math.round(rating)));
}

export function sanitizeReviewText(comment: string): string {
  return comment
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email redacted]')
    .replace(/(?<!\d)(?:\+?\d[\d\s().-]{7,}\d)(?!\d)/g, '[phone redacted]')
    .replace(/\s+/g, ' ')
    .trim();
}

export function classifyReview(review: Pick<Review, 'rating' | 'comment' | 'createdAt'>): ReviewAnalysis {
  const rating = clampRating(review.rating);
  const lowered = review.comment.toLowerCase();
  const highRiskHits = HIGH_RISK_TERMS.filter((term) => lowered.includes(term));
  const mediumRiskHits = MEDIUM_RISK_TERMS.filter((term) => lowered.includes(term));
  const tags = CATEGORY_RULES.filter((rule) => rule.needles.some((needle) => lowered.includes(needle))).map((rule) => rule.tag);
  const uniqueTags = [...new Set(tags.length > 0 ? tags : rating <= 3 ? ['experience'] : ['praise'])];
  const ageHours = Math.max(0, (Date.now() - new Date(review.createdAt).getTime()) / 3_600_000);

  let sentiment: Sentiment = 'neutral';
  if (rating >= 4 && highRiskHits.length === 0 && mediumRiskHits.length === 0) sentiment = 'positive';
  if (rating <= 2 || highRiskHits.length > 0 || mediumRiskHits.length > 1) sentiment = 'negative';

  let risk: RiskLevel = 'low';
  if (rating <= 2 || mediumRiskHits.length > 0) risk = 'medium';
  if (
    highRiskHits.length > 0 ||
    (rating === 1 && mediumRiskHits.length >= 2) ||
    (rating <= 2 && uniqueTags.includes('safety')) ||
    (rating === 1 && uniqueTags.includes('loyalty'))
  ) {
    risk = 'high';
  }

  let urgency: Urgency = 'low';
  if (risk === 'medium' || rating <= 3) urgency = 'medium';
  if (risk === 'high' || (rating <= 2 && ageHours < 48)) urgency = 'high';

  const reasons: string[] = [];
  if (rating <= 2) reasons.push('Low star rating needs a careful human-reviewed reply.');
  if (highRiskHits.length > 0) reasons.push(`Potential policy/legal/safety terms: ${highRiskHits.slice(0, 3).join(', ')}.`);
  if (mediumRiskHits.length > 0) reasons.push(`Customer frustration terms: ${mediumRiskHits.slice(0, 3).join(', ')}.`);
  if (risk === 'high' && highRiskHits.length === 0) reasons.push('Multiple serious complaint signals make this high priority for owner review.');
  if (ageHours > 48 && review.rating <= 3) reasons.push('Older unresolved critical review may signal slow response.');
  if (reasons.length === 0) reasons.push(sentiment === 'positive' ? 'Positive review suitable for quick gratitude.' : 'Neutral review benefits from a specific acknowledgement.');

  const score = Math.min(
    100,
    Math.max(0, rating * 15 + (sentiment === 'positive' ? 20 : sentiment === 'neutral' ? 8 : 0) - highRiskHits.length * 18 - mediumRiskHits.length * 10)
  );

  return {
    sentiment,
    risk,
    urgency,
    score,
    tags: uniqueTags,
    reasons,
    redactedComment: sanitizeReviewText(review.comment),
    recommendedTone: risk === 'high' ? 'professional' : sentiment === 'positive' ? 'warm' : 'concise',
    timeToRespondHours: ageHours
  };
}

export function buildDraftResponse(review: Review, profile: BusinessProfile = DEFAULT_PROFILE): string {
  const analysis = classifyReview(review);
  const name = review.reviewerName.trim() ? review.reviewerName.trim().split(' ')[0] : 'there';
  const business = profile.businessName.trim() || 'our team';
  const contactLine = `Please contact ${business} directly with your visit details so a manager can review this properly before any next step is taken.`;

  if (analysis.sentiment === 'positive') {
    const opening = profile.brandVoice === 'concise' ? `Hi ${name}, thank you for the ${review.rating}-star review.` : `Hi ${name}, thank you for taking the time to share this ${review.rating}-star review.`;
    return `${opening} We are glad the experience matched what you expected from ${business}. We appreciate your trust and hope to welcome you again soon.`;
  }

  if (analysis.risk === 'high') {
    return `Hi ${name}, thank you for raising this. We are sorry to hear about your experience and we do not want to make assumptions in a public reply. ${contactLine} We will treat this with care and review it internally.`;
  }

  if (analysis.sentiment === 'negative') {
    return `Hi ${name}, thank you for the feedback. We are sorry the experience did not meet expectations, especially around ${analysis.tags.slice(0, 2).join(' and ')}. ${contactLine} Your feedback helps us identify what needs attention.`;
  }

  return `Hi ${name}, thank you for sharing this. We appreciate the balanced feedback and will use it to improve the experience at ${business}. Please contact us directly if there is anything specific you would like our team to review.`;
}

export function needsHumanReview(review: Review): boolean {
  const analysis = classifyReview(review);
  return analysis.risk === 'high' || analysis.urgency === 'high' || analysis.redactedComment.includes('[email redacted]') || analysis.redactedComment.includes('[phone redacted]');
}

export function parseCsvReviews(csv: string): Review[] {
  const rows = csv
    .split(/\r?\n/)
    .map((row) => row.trim())
    .filter(Boolean);

  if (rows.length <= 1) return [];

  const headers = rows[0].split(',').map((header) => header.trim().toLowerCase());
  return rows.slice(1).map((row, index) => {
    const values = row.split(',').map((value) => value.trim());
    const get = (name: string, fallback = '') => values[headers.indexOf(name)] ?? fallback;
    const source = (get('source', 'Manual') || 'Manual') as ReviewSource;
    return {
      id: `csv-${Date.now()}-${index}`,
      source: ['Google', 'Yelp', 'Trustpilot', 'Facebook', 'Manual'].includes(source) ? source : 'Manual',
      location: get('location', 'Imported location'),
      reviewerName: get('reviewer', 'Customer'),
      rating: clampRating(Number(get('rating', '3'))),
      comment: get('comment', 'No comment provided.'),
      createdAt: get('createdat', new Date().toISOString()),
      status: 'new'
    };
  });
}

export function aggregateMetrics(reviews: Review[], profile: BusinessProfile = DEFAULT_PROFILE): Metrics {
  const total = reviews.length;
  const analyses = reviews.map((review) => classifyReview(review));
  const unhandled = reviews.filter((review) => !['approved', 'published'].includes(review.status)).length;
  const highRisk = analyses.filter((analysis) => analysis.risk === 'high').length;
  const replied = reviews.filter((review) => ['approved', 'published'].includes(review.status)).length;
  const approved = reviews.filter((review) => review.status === 'approved' || review.status === 'published').length;
  const averageRating = total === 0 ? 0 : reviews.reduce((sum, review) => sum + clampRating(review.rating), 0) / total;
  const monthlyHoursSaved = Math.round(((profile.monthlyReviews * 7) / 60) * 10) / 10;
  const monthlyLaborValue = Math.round(monthlyHoursSaved * profile.laborCostPerHour);
  const unresolvedCriticalShare = total === 0 ? 0 : highRisk / total;
  const revenueAtRisk = Math.round(profile.averageTicket * Math.max(1, profile.monthlyReviews * unresolvedCriticalShare) * 1.8);

  return {
    totalReviews: total,
    unhandledReviews: unhandled,
    highRiskReviews: highRisk,
    responseCoverage: total === 0 ? 0 : Math.round((replied / total) * 100),
    approvalRate: total === 0 ? 0 : Math.round((approved / total) * 100),
    averageRating: Math.round(averageRating * 10) / 10,
    estimatedMonthlyHoursSaved: monthlyHoursSaved,
    estimatedMonthlyLaborValue: monthlyLaborValue,
    estimatedRevenueAtRisk: revenueAtRisk,
    roiNarrative: `At ${profile.monthlyReviews} reviews/month, saving about 7 minutes per response creates ~${monthlyHoursSaved} staff hours/month before considering churn avoided from faster critical-review handling.`
  };
}

export function serializeReport(reviews: Review[], profile: BusinessProfile): string {
  const metrics = aggregateMetrics(reviews, profile);
  const lines = [
    `ReviewReply report for ${profile.businessName}`,
    `Location: ${profile.location}`,
    `Coverage: ${metrics.responseCoverage}%`,
    `High-risk open reviews: ${metrics.highRiskReviews}`,
    `Estimated monthly labor value: €${metrics.estimatedMonthlyLaborValue}`,
    `Estimated revenue at risk: €${metrics.estimatedRevenueAtRisk}`,
    '',
    'Open review queue:'
  ];

  reviews
    .filter((review) => review.status !== 'published')
    .forEach((review) => {
      const analysis = classifyReview(review);
      lines.push(`- [${analysis.urgency}/${analysis.risk}] ${review.source} ${review.rating}★ ${review.reviewerName}: ${analysis.redactedComment}`);
    });

  return lines.join('\n');
}
